import { useMemo } from "react";

import { useDispatch } from "react-redux";

import { swSettings } from "app/common/services/swSettings";
import { ADDON_TYPE_NAME_DICT } from "app/contexts/AddonStateContext/const";
import { ADDON_HUB_PRESELECT_DEFAULTS } from "app/contexts/NoTouchPurchaseContext/useNoTouchPurchaseProvider";
import type { LowCreditsBannerProps } from "app/pages/sales-intelligence/modules/quota/components/LowCreditsBanner/LowCreditsBanner";
import {
    adminInsufficientCreditsBannerConfigs,
    adminLowCreditsBannerConfigs,
    noTouchContactsRevealCreditsBannerConfigs,
    noTouchInsufficientCreditsBannerConfigs,
    noTouchLowCreditsBannerConfigs,
    trialInsufficientCreditsBannerConfigs,
    trialLowCreditsBannerConfigs,
    userInsufficientCreditsBannerConfigs,
    userLowCreditsBannerConfigs,
} from "app/pages/sales-intelligence/modules/quota/constants/configuration";
import { useExportQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useExportQuota";
import { useNotifyAdminAboutSmallQuota } from "app/pages/sales-intelligence/modules/quota/queries/useNotifyAdminAboutSmallQuota";
import { setQuotaContactAdminModalOpen } from "app/pages/sales-intelligence/modules/quota/store";
import type { LowQuotaBannerDestinationType } from "app/pages/sales-intelligence/modules/quota/types";
import { openTrialUpgradeUrl } from "app/pages/sales-intelligence/pages/premium-features/trial";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import solutionUrlsService from "app/pages/sales-intelligence/services/solution-urls";
import { goToAddonsPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToAddonsPage";
import { hasAddonsEnabled, isNoTouch, isTrial } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

type HookValue = {
    lowCreditsBannerConfigs: LowCreditsBannerProps;
    quotaManagementLink: string;
};

export const useLowQuotaBannerManagement = (type?: LowQuotaBannerDestinationType): HookValue => {
    const dispatch = useDispatch();
    const { mutate: notifyAdmin, isPending } = useNotifyAdminAboutSmallQuota();
    const { data } = useExportQuota();
    const hasPartialCredits = (data?.remaining ?? 0) > 0;

    const onRedirectToDataManagement = () => window.open(solutionUrlsService.getAccountDataCreditsUrl());

    const onRedirectToTrialUpgrade = () => openTrialUpgradeUrl("low-quota-banner");

    const lowCreditsBannerConfigs = useMemo(() => {
        if (isTrial(swSettings)) {
            const trialConfigs = hasPartialCredits
                ? trialInsufficientCreditsBannerConfigs
                : trialLowCreditsBannerConfigs;
            return { ...trialConfigs, isTrial: true, onClick: onRedirectToTrialUpgrade };
        }

        if (isNoTouch(swSettings)) {
            const configNT = hasPartialCredits
                ? type === "reveal"
                    ? noTouchContactsRevealCreditsBannerConfigs
                    : noTouchInsufficientCreditsBannerConfigs
                : noTouchLowCreditsBannerConfigs;
            return {
                ...configNT,
                onClick: () => {
                    if (!hasAddonsEnabled(swSettings)) {
                        return;
                    }
                    goToAddonsPage({
                        packageIds: [ADDON_HUB_PRESELECT_DEFAULTS[ADDON_TYPE_NAME_DICT.SiDataCredits1000]],
                    });
                },
            };
        }

        const adminConfigs = hasPartialCredits ? adminInsufficientCreditsBannerConfigs : adminLowCreditsBannerConfigs;
        if (salesSettingsHelper.isAdmin()) {
            return { ...adminConfigs, onClick: onRedirectToDataManagement };
        }

        const userConfigs = hasPartialCredits ? userInsufficientCreditsBannerConfigs : userLowCreditsBannerConfigs;
        return {
            ...userConfigs,
            isLoading: isPending,
            onClick: () => {
                notifyAdmin();
                dispatch(setQuotaContactAdminModalOpen(true));
            },
        };
    }, [isPending, notifyAdmin, dispatch, hasPartialCredits, type]);

    return {
        lowCreditsBannerConfigs,
        get quotaManagementLink() {
            return solutionUrlsService.getAccountDataCreditsUrl();
        },
    };
};
